/**
 * TODO
 * variable picker with special data types?
 * better does/is wording for operators
 */

import type {
  ConditionNode,
  Id,
  EditableGraph,
  ResponseType,
} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';
import type {
  Ruleset,
  EditableRuleset,
  EditableExpression,
  Operator,
} from 'src/types/ruleset';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import invariant from 'invariant';

import {
  useAtsVariables,
  useEditorState,
  writebackDataTypeToResponseType,
} from './reducer';
import generateId from 'src/utils/id';
import {setError, type Validation} from 'src/utils/validation';
import {keyBy} from 'src/utils/map';
import {validateNode} from './validation';
import {
  getOperatorOptions,
  getStringOperatorOptions,
  getNumericOperatorOptions,
  dateResponseTypes,
  numericResponseTypes,
  getBooleanOptions,
  findOption,
  updateExpression,
  createExpression,
  validateRuleset,
  initializeEditableRuleset,
} from '../utils';

import {Menu, Select} from 'src/components/lib/token-list-input/borderless';
import EditorContainer from './container';
import {PadSection} from './common';
import {UnstyledButton} from 'src/components/lib/new-button';
import VariablePickerSelect from 'src/components/lib/variable-picker/select-2';

import css from './condition.css';
import borderlessClassNames from 'src/components/lib/token-list-input/borderless.css';


export default function ConditionEditor({
  entityType,
  defaultNode,
  onSubmit,
  graph,
  hasSubmitted,
  ...props
}: {
  entityType: EntityType;
  onClose: () => unknown;
  onSubmit: (arg1: ConditionNode) => unknown;
  defaultNode: ConditionNode;
  actionName: 'add' | 'edit';
  graph: EditableGraph;
  hasSubmitted: boolean;
  readOnly: boolean;
}) {
  const {t, makeKey} = useLabelTranslation();
  const {readOnly} = props;
  const numericOperatorOptions = getNumericOperatorOptions(t);
  const stringOperatorOptions = getStringOperatorOptions(t);
  const operatorOptions = getOperatorOptions(t);

  const dynamicLabels = useAtsVariables(entityType);
  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const dynamicLabelMap = React.useMemo(() => keyBy(dynamicLabels, 'value'));

  const [state, dispatch] = useEditorState<ConditionNode>({
    defaultNode,
  });

  const update = (payload: Partial<ConditionNode>) =>
    dispatch({type: 'update', payload});

  // @ts-expect-error - TS2345 - Argument of type '() => { expressions: EditableExpression[]; id: number; }' is not assignable to parameter of type 'EditableRuleset | (() => EditableRuleset)'.
  const [ruleset, setRuleset] = React.useState<EditableRuleset>(() => {
    const {condition_ruleset} = state.node;
    const {expressions} = condition_ruleset;
    return expressions.length > 0
      ? initializeEditableRuleset(condition_ruleset)
      : {...condition_ruleset, expressions: [createExpression()]};
  });
  // @ts-expect-error - TS2345 - Argument of type 'Map<unknown, any>' is not assignable to parameter of type '{ [key: string]: VariablePickerDynamicLabels; }'.
  const validation = validateRuleset(ruleset, dynamicLabelMap);
  const {expressions} = ruleset;

  // @ts-expect-error - TS7006 - Parameter 'updates' implicitly has an 'any' type.
  const updateRuleset = (expressionId: Id, updates) => {
    setRuleset(updateExpression(ruleset, expressionId, updates));
  };

  const nodeOptions = React.useMemo(
    () =>
      [...graph.nodes.values()]
        .filter((node) => node.type === 'question')
        .map((node) => ({
          label: node.name,
          value: node.id,
        })),
    [graph.nodes],
  );

  const error = hasSubmitted
    ? validateNode(
        {
          ...state.node,
          condition_ruleset: ruleset,
        },
        graph,
      )
    : null;

  return (
    <EditorContainer
      className={css.popover}
      onUpdate={update}
      state={state}
      onSubmit={(node) => {
        if (!validation) {
          onSubmit({
            ...node,
            condition_ruleset: ruleset,
          });
        }
      }}
      disabled={validation}
      error={error}
      {...props}
    >
      <PadSection>
        <div className={css.root}>
          <div className={css.ruleset}>
            {expressions.map((expression, index) => {
              const condition_value =
                expression.condition_value instanceof Array
                  ? expression.condition_value
                  : [expression.condition_value].filter(Boolean);

              let dataType = null;
              if (expression.node_id_answer) {
                const node = graph.nodes.get(expression.node_id_answer);
                invariant(
                  node?.type === 'question',
                  'Node in an expression must be a `question`.',
                );
                dataType = node.response_type;
              } else {
                const dynamicLabel = dynamicLabelMap.get(
                  expression.dynamic_field,
                );
                const atsDataType = dynamicLabel?.type;
                dataType =
                  atsDataType && atsDataType in writebackDataTypeToResponseType
                    ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Partial<Record<WritebackDataType, ResponseType>>'.
                      writebackDataTypeToResponseType[atsDataType]
                    : null;
              }

              if (!dataType) {
                dataType = 'string';
              }

              const isDate = dateResponseTypes.has(dataType);
              const isNumeric = numericResponseTypes.has(dataType);
              const operatorOptionsForDataType = isNumeric
                ? numericOperatorOptions
                : dataType === 'string'
                ? stringOperatorOptions
                : operatorOptions;

              const notOptions = getBooleanOptions(t, expression.operator);

              // for numbers, when we pick gt/lt we can only have one option
              const varPickerOptionsLimited =
                dataType === 'number' &&
                ['greater_than', 'less_than'].includes(expression.operator);

              return (
                <div className={css.expressionContainer}>
                  <div className={css.expression} key={expression.id}>
                    <div className={css.prefix}>
                      {!readOnly && ruleset.expressions.length > 1 ? (
                        <UnstyledButton
                          className={css.remove}
                          onClick={() => {
                            setRuleset({
                              ...ruleset,
                              expressions: ruleset.expressions.filter(
                                ({id}) => id !== expression.id,
                              ),
                            });
                          }}
                        >
                          {t('x', 'x')}
                        </UnstyledButton>
                      ) : (
                        <div />
                      )}

                      <span className={css.if}>
                        {index === 0 ? t('If', 'If') : t('And', 'And')}
                      </span>
                    </div>

                    {
                      // TODO (kyle): this ternary swapping is pretty inefficent and
                      // could be accomplished with a custom component. the token-list-input/hooks
                      // would be helpful here.
                    }
                    {'node_id_answer' in expression ? (
                      // @ts-expect-error - TS2741 - Property 'resolveLabel' is missing in type '{ searchable: true; autofocus: boolean; placeholder: string; options: { label: string; value: number; }[]; value: { label: string; value: number; } | null | undefined; onChange: (option: { label: string; value: number; } | null | undefined) => void; disabled: boolean; resolveKey: (option: { ...; }) => string; }' but required in type 'SenseTypeaheadProps<{ label: string; value: number; }>'.
                      <Select
                        searchable
                        autofocus={!expression.node_id_answer}
                        placeholder="Select node"
                        options={nodeOptions}
                        value={findOption(
                          nodeOptions,
                          expression.node_id_answer,
                        )}
                        onChange={(option) => {
                          updateRuleset(expression.id, {
                            node_id_answer: option?.value,
                          });
                        }}
                        disabled={readOnly}
                        resolveKey={(option) => String(option.value)}
                      />
                    ) : 'dynamic_field' in expression ? (
                      // @ts-expect-error - TS2739 - Type '{ baseEntityType: EntityType; searchable: true; autofocus: boolean; baseClassNames: { [className: string]: string; }; placeholder: string; limit: number; values: any[]; options: DynamicLabels; onChange: (options: any[]) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<any>': resolveKey, resolveLabel
                      <VariablePickerSelect
                        baseEntityType={entityType}
                        searchable
                        // @ts-expect-error - TS2339 - Property 'dynamic_field' does not exist on type 'never'.
                        autofocus={!expression.dynamic_field}
                        baseClassNames={borderlessClassNames}
                        placeholder="Search variables"
                        limit={1}
                        values={[
                          // @ts-expect-error - TS2339 - Property 'dynamic_field' does not exist on type 'never'.
                          dynamicLabelMap.get(expression.dynamic_field),
                        ].filter(Boolean)}
                        options={dynamicLabels}
                        onChange={(options) => {
                          // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                          updateRuleset(expression.id, {
                            dynamic_field: options[0]?.value,
                          });
                        }}
                        disabled={readOnly}
                      />
                    ) : (
                      <Menu
                        placeholder="Node or var"
                        options={[
                          {value: 'node', label: 'Node'},
                          {value: 'variable', label: 'Variable'},
                        ]}
                        onChange={(option) => {
                          if (option.value === 'node') {
                            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                            updateRuleset(expression.id, {
                              node_id_answer: null,
                            });
                          } else if (option.value === 'variable') {
                            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                            updateRuleset(expression.id, {
                              dynamic_field: null,
                            });
                          }
                        }}
                        value={null}
                        disabled={readOnly}
                      />
                    )}

                    <Menu
                      options={notOptions}
                      value={expression.is_not}
                      onChange={(option) => {
                        updateRuleset(expression.id, {is_not: option.value});
                      }}
                      disabled={readOnly}
                    />

                    <Menu
                      options={operatorOptionsForDataType}
                      value={expression.operator}
                      onChange={(option) => {
                        updateRuleset(expression.id, {operator: option.value});
                      }}
                      disabled={readOnly}
                    />
                    {/* @ts-expect-error - TS2739 - Type '{ classNames: { outerContainer: string; }; inputProps: { type: "number" | "text" | "date"; }; baseEntityType: EntityType; isExpandable: true; searchable: true; searchOnEmptyString: false; ... 8 more ...; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<any>': resolveKey, resolveLabel */}
                    <VariablePickerSelect
                      classNames={{outerContainer: css.bumpedSelect}}
                      inputProps={{
                        type: isDate ? 'date' : isNumeric ? 'number' : 'text',
                      }}
                      baseEntityType={entityType}
                      isExpandable
                      searchable
                      searchOnEmptyString={false}
                      allowArbitraryValues
                      // when options are limited to 1 set this to false
                      // so that a phantom input doesn't appear
                      showValuesOutside={!varPickerOptionsLimited}
                      limit={varPickerOptionsLimited ? 1 : undefined}
                      baseClassNames={borderlessClassNames}
                      placeholder={!isDate ? 'Empty' : null}
                      values={condition_value
                        .map((value) => {
                          const variable = value.match(/^<(.*)>$/);
                          if (variable) {
                            return dynamicLabelMap.get(variable[1]);
                          }
                          return {value, arbitrary: true, label: value};
                        })
                        .filter(Boolean)}
                      options={dynamicLabels.filter(
                        (label) =>
                          dataType === 'string' || label.type === dataType,
                      )}
                      onChange={(options) =>
                        updateRuleset(expression.id, {
                          condition_value:
                            options.length > 0
                              ? options.map((option) =>
                                  option.arbitrary
                                    ? option.value
                                    : `<${option.value}>`,
                                )
                              : [],
                        })
                      }
                      disabled={readOnly}
                    />
                  </div>
                  {error?.expressions?.[String(index)]?.errors?.map(
                    (err: string) => (
                      <div className={css.errorText}>
                        {t(makeKey(err), err)}
                      </div>
                    ),
                  )}
                </div>
              );
            })}
          </div>

          {!readOnly && (
            <UnstyledButton
              className={css.addButton}
              onClick={() => {
                setRuleset({
                  ...ruleset,
                  // @ts-expect-error - TS2322 - Type 'EditableExpression | (BaseExpression & { operator: Operator; node_id_answer: number; dynamic_field: string | null | undefined; condition_value: Value; } & { ...; })' is not assignable to type 'BaseExpression & { operator: Operator; node_id_answer: number; dynamic_field: string | null | undefined; condition_value: Value; } & { operator: Operator | ... 1 more ... | undefined; node_id_answer?: number | ... 1 more ... | undefined; dynamic_field?: string | ... 1 more ... | undefined; condition_value: Value; }'. | TS2322 - Type 'EditableExpression' is not assignable to type 'BaseExpression & { operator: Operator; node_id_answer: number; dynamic_field: string | null | undefined; condition_value: Value; } & { operator: Operator | ... 1 more ... | undefined; node_id_answer?: number | ... 1 more ... | undefined; dynamic_field?: string | ... 1 more ... | undefined; condition_value: Value; }'.
                  expressions: [...expressions, createExpression()],
                });
              }}
            >{`+ ${t('And', 'And')}`}</UnstyledButton>
          )}
        </div>
      </PadSection>
    </EditorContainer>
  );
}
